import type React from "react";
import { Box, Typography, Paper } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import type { Proposal } from "../types/proposal.types";
import FuneralPreviewCard from "./components/FuneralPreviewThumbnail";
import { formatDateToLongString } from "../../../utils/FormatDate";

interface WhatsAppPreviewPageProps {
  proposal: Proposal;
}

const WhatsAppPreviewPage: React.FC<WhatsAppPreviewPageProps> = ({
  proposal,
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const deceasedFullName = `${
    proposal.booking?.deceased?.[0]?.first_name || proposal.deceasedName || ""
  } ${proposal.booking?.deceased?.[0]?.surname || ""}`.trim();

  const serviceDate = formatDate(
    proposal.booking?.services?.[0]?.date_of_service
  );
  const serviceTime = proposal.booking?.services?.[0]?.start_time || "";
  const venueName = proposal.booking?.services?.[0]?.venue.name || "";
  const venueAddress = proposal.booking?.services?.[0]?.venue.address_one || "";
  const venueCity = proposal.booking?.services?.[0]?.venue.city || "";

  // Generate memorial link (you can customize this based on your actual URL structure)
  const memorialLink = `https://muzukuru.com/memorial-page/${
    proposal.id
  }/${deceasedFullName.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <Box
      id="whatsapp-page"
      sx={{
        p: 6,
        height: "1056px",
        width: "816px",
        overflow: "hidden",
      }}
    >
      {/* Header Section */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <WhatsAppIcon sx={{ fontSize: 80, color: "#25D366" }} />
        </Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 2,
            textTransform: "uppercase",
            lineHeight: 1.3,
          }}
        >
          MANY OF THE FAMILIES WE WORK WITH USE
          <br />
          WHATSAPP AS A PRIMARY SOURCE OF COMMUNICATIONS
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 400, color: "#666" }}>
          OUR TEAM WILL CREATE A MESSAGE WHICH YOU CAN USE - YOU CAN ALSO EDIT
          IT TO YOUR OWN PREFERENCE
        </Typography>
      </Box>

      {/* Mobile Mockup */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 4,
          position: "relative",
        }}
      >
        {/* Label: THUMBNAIL */}
        <Box
          sx={{
            position: "absolute",
            left: "5px",
            top: "120px",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "24px",
              textTransform: "uppercase",
            }}
          >
            THUMBNAIL
          </Typography>
          <Box
            sx={{
              width: "20px",
              height: "3px",
              backgroundColor: "#000",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: "-4px",
                width: 0,
                height: 0,
                borderLeft: "8px solid #000",
                borderTop: "5px solid transparent",
                borderBottom: "5px solid transparent",
              },
            }}
          />
        </Box>

        {/* Label: VENUE */}
        <Box
          sx={{
            position: "absolute",
            left: "50px",
            top: "430px",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "24px",
              textTransform: "uppercase",
            }}
          >
            VENUE
          </Typography>
          <Box
            sx={{
              width: "30px",
              height: "3px",
              backgroundColor: "#000",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: "-4px",
                width: 0,
                height: 0,
                borderLeft: "8px solid #000",
                borderTop: "5px solid transparent",
                borderBottom: "5px solid transparent",
              },
            }}
          />
        </Box>

        {/* Label: LINK */}
        <Box
          sx={{
            position: "absolute",
            left: "30px",
            top: "500px",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "24px",
              textTransform: "uppercase",
            }}
          >
            LINK
          </Typography>
          <Box
            sx={{
              width: "80px",
              height: "3px",
              backgroundColor: "#000",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: "-4px",
                width: 0,
                height: 0,
                borderLeft: "8px solid #000",
                borderTop: "5px solid transparent",
                borderBottom: "5px solid transparent",
              },
            }}
          />
        </Box>

        {/* Label: INVITE TO COMMENT (Right side) */}
        {/* <Box
          sx={{
            position: "absolute",
            right: "50px",
            top: "540px",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: "80px",
              height: "3px",
              backgroundColor: "#000",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: "-4px",
                width: 0,
                height: 0,
                borderRight: "8px solid #000",
                borderTop: "5px solid transparent",
                borderBottom: "5px solid transparent",
              },
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "24px",
              textTransform: "uppercase",
              textAlign: "right",
              lineHeight: 1.2,
            }}
          >
            INVITE TO
            <br />
            COMMENT
          </Typography>
        </Box> */}

        <Paper
          elevation={8}
          sx={{
            width: "350px",
            height: "650px",
            borderRadius: "30px",
            border: "12px solid #1a1a1a",
            backgroundColor: "#e5ddd5",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* WhatsApp Header */}
          <Box
            sx={{
              backgroundColor: "#075E54",
              color: "white",
              p: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "12px" }}>
              CLIIMZ|Jessinah Kuwaza
            </Typography>
          </Box>

          {/* Message Content */}
          <Box
            sx={{
              p: 2,
              height: "calc(100% - 100px)",
              overflowY: "auto",
              backgroundColor: "#e5ddd5",
            }}
          >
            {/* Message Bubble */}
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                p: 2,
                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              }}
            >
              {/* Thumbnail */}
              <Box
                sx={{
                  mb: 2,
                  "& > div": {
                    transform: "scale(0.75)",
                    transformOrigin: "top left",
                    width: "133%",
                  },
                }}
              >
                <FuneralPreviewCard
                  deceasedName={deceasedFullName}
                  birthDate={formatDateToLongString(
                    proposal.booking?.deceased?.[0]?.date_of_birth ||
                      proposal.deceasedBirthDate ||
                      ""
                  )}
                  deathDate={formatDateToLongString(
                    proposal.booking?.deceased?.[0]?.date_of_death ||
                      proposal.deceasedPassedDate ||
                      ""
                  )}
                  serviceDate={formatDateToLongString(
                    proposal.booking?.services?.[0]?.date_of_service || ""
                  )}
                  serviceTime={serviceTime}
                  timeZone="CAT"
                  location={`${venueName}, ${venueAddress}`}
                  isLiveStream={true}
                  broadcastBy="MUZUKURU"
                />
              </Box>

              {/* Message Text */}
              <Typography variant="body2" sx={{ fontSize: "13px", mb: 1.5 }}>
                Dear Family & Friends,
              </Typography>

              <Typography variant="body2" sx={{ fontSize: "13px", mb: 1.5 }}>
                You're invited to virtually celebrate the life of{" "}
                <strong>{deceasedFullName}</strong>.
              </Typography>

              <Typography
                variant="body2"
                sx={{ fontSize: "13px", fontWeight: 600, mb: 0.5 }}
              >
                Memorial Service Details:
              </Typography>

              <Box sx={{ ml: 0, mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontSize: "13px" }}>
                  <strong>Date:</strong> {serviceDate}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "13px" }}>
                  <strong>Time:</strong> {serviceTime}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "13px" }}>
                  <strong>Venue Name:</strong> {venueName}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "13px" }}>
                  <strong>Address:</strong> {venueAddress}
                  {venueCity && `, ${venueCity}`}
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{ fontSize: "13px", fontWeight: 600, mb: 0.5 }}
              >
                Link details:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "13px",
                  color: "#1976d2",
                  wordBreak: "break-all",
                  mb: 1.5,
                }}
              >
                {memorialLink}
              </Typography>

              <Typography variant="body2" sx={{ fontSize: "13px", mb: 1.5 }}>
                Your presence will mean so much to us all.
              </Typography>

              <Typography
                variant="body2"
                sx={{ fontSize: "11px", color: "#999", textAlign: "right" }}
              >
                08:13
              </Typography>
            </Box>

            {/* Mention Message */}
          </Box>

          {/* Input Footer */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "#f0f0f0",
              p: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                flex: 1,
                backgroundColor: "white",
                borderRadius: "20px",
                p: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: "13px", color: "#999", ml: 1 }}
              >
                Type a message
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default WhatsAppPreviewPage;
